import { Component, inject, OnInit, signal, computed } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FirmaService } from '../../../shared/services/firma.service';
import { AuthService } from '../../../shared/services/auth.service';
import { PraticaDaFirmare } from '../../../shared/models/pratica.model';
import { HeaderComponent } from '../shared/components/header/header.component';
import { ModaleOtpComponent } from '../../../shared/components/modali/modale-otp/modale-otp.component';
import { ModaleErroreGenericoComponent } from '../../../shared/components/modali/modale-errore-generico/modale-errore-generico.component';
import { ModaleConfermaAzioneComponent } from '../../../shared/components/modali/modale-conferma-azione/modale-conferma-azione.component';
import { ModaleSuccessoComponent } from '../../../shared/components/modali/modale-successo/modale-successo.component';
import { CommonModule } from '@angular/common';

@Component({
	selector: 'app-home',
	standalone: true,
	imports: [CommonModule, HeaderComponent],
	templateUrl: './home.component.html',
})
export class HomeComponent implements OnInit {
	router = inject(Router);
	firmaService = inject(FirmaService);
	authService = inject(AuthService);
	modalService = inject(NgbModal);

	pratiche = signal<PraticaDaFirmare[]>([]);
	loading = signal<boolean>(true);
	isActionLoading = signal<boolean>(false);
	
	// Multi-selection signals
	selectedIds = signal<number[]>([]);
	isSelectionMode = signal<boolean>(false);

	userName = computed(() => {
		const u = this.authService.currentUser;
		return u ? `${u.nome} ${u.cognome}` : '';
	});

	userEmail = computed(() => {
		return this.authService.currentUser?.user ?? '';
	});

	ngOnInit() {
		this.loadPratiche();
	}

	loadPratiche() {
		this.loading.set(true);
		this.firmaService.listPraticheDaFirmare().subscribe({
			next: (res: any) => {
				this.pratiche.set(res?.pratiche ?? []);
				this.loading.set(false);
			},
			error: () => {
				this.pratiche.set([]);
				this.loading.set(false);
			},
		});
	}

	toggleSelection(id: number, event?: Event) {
		if (event) {
			event.stopPropagation();
		}
		if (!this.isSelectionMode()) {
			this.isSelectionMode.set(true);
		}
		
		const current = this.selectedIds();
		if (current.includes(id)) {
			this.selectedIds.set(current.filter(i => i !== id));
			if (this.selectedIds().length === 0) {
				this.isSelectionMode.set(false);
			}
		} else {
			if (current.length >= 5) {
				this.showError('Limite raggiunto', 'Puoi selezionare al massimo 5 pratiche contemporaneamente.');
				return;
			}
			this.selectedIds.set([...current, id]);
		}
	}

	cancelSelection() {
		this.selectedIds.set([]);
		this.isSelectionMode.set(false);
	}

	signSelected() {
		const selected = this.selectedIds();
		if (selected.length === 0) return;

		const isMultiple = selected.length > 1;
		const confModal = this.modalService.open(ModaleConfermaAzioneComponent, { centered: true });
		confModal.componentInstance.title = 'Attenzione';
		confModal.componentInstance.message = isMultiple 
			? 'Stai per firmare più pratiche in modo automatico. Proseguendo, confermi di accettare integralmente il contenuto dei documenti e dichiari di assumerti ogni responsabilità derivante dalla loro firma.'
			: 'Stai per firmare la pratica in modo automatico. Proseguendo, confermi di accettare integralmente il contenuto dei documenti e dichiari di assumerti ogni responsabilità derivante dalla loro firma.';

		confModal.result.then((confirmed) => {
			if (confirmed) {
				this.executeMultiSigning(selected);
			}
		}, () => {});
	}

	executeMultiSigning(ids: number[]) {
		const user = this.authService.currentUser;
		if (!user) return;

		this.isActionLoading.set(true);
		
		if (!user.flgSignRemota) {
			// OTP Flow
			this.firmaService.startMultiOtp(ids).subscribe({
				next: () => {
					this.isActionLoading.set(false);
					this.showOtpModal('sms', user.cellulare, (otp) => {
						this.isActionLoading.set(true);
						this.firmaService.signMultiOtp(ids, otp).subscribe({
							next: () => this.handleSuccess(),
							error: (err) => this.handleError(err)
						});
					});
				},
				error: (err) => this.handleError(err)
			});
		} else {
			// Remote Flow
			this.isActionLoading.set(false);
			this.showOtpModal('remote', '', (otp) => {
				this.isActionLoading.set(true);
				this.firmaService.signMultiRemota(ids, otp).subscribe({
					next: () => this.handleSuccess(),
					error: (err) => this.handleError(err)
				});
			});
		}
	}

	showOtpModal(mode: 'sms' | 'remote', phone: string, onConfirm: (otp: string) => void) {
		const modalRef = this.modalService.open(ModaleOtpComponent, {
			centered: true,
			backdrop: 'static',
			keyboard: false,
			windowClass: 'modal-otp-custom',
		});
		modalRef.componentInstance.mode = mode;
		modalRef.componentInstance.phone = phone || '';
		
		modalRef.result.then((otpCode) => {
			if (otpCode) {
				onConfirm(otpCode);
			}
		}, () => {});
	}

	handleSuccess() {
		const count = this.selectedIds().length;
		this.isActionLoading.set(false);
		this.selectedIds.set([]);
		this.isSelectionMode.set(false);
		this.loadPratiche();
		
		const modalRef = this.modalService.open(ModaleSuccessoComponent, { centered: true });
		modalRef.componentInstance.title = 'Firma Completata';
		modalRef.componentInstance.message = count > 1 
			? 'Tutte le pratiche selezionate sono state firmate con successo.'
			: 'La pratica selezionata è stata firmata con successo.';
	}

	handleError(err: any) {
		const count = this.selectedIds().length;
		this.isActionLoading.set(false);
		const msg = err?.error?.message || (count > 1 
			? 'Si è verificato un errore durante la firma multipla.' 
			: 'Si è verificato un errore durante la firma.');
		this.showError('Errore', msg);
	}

	openPratica(pratica: PraticaDaFirmare) {
		const id = pratica.idTransaction;
		const cachedOpId = this.firmaService.getAuthOperationID(id);
		if (cachedOpId) {
			this.navigateToDettaglio(pratica, cachedOpId, true);
			return;
		}

		this.isActionLoading.set(true);
		this.firmaService.getPraticaDetail(id).subscribe({
			next: (statusRes) => {
				const token = statusRes.operationToken;
				
				this.firmaService.startAuthOperation(id, token).subscribe({
					next: (authRes: any) => {
						const opId = authRes.operationID;
						this.isActionLoading.set(false);
						
						if (statusRes.authFirst) {
							const modalRef = this.modalService.open(ModaleOtpComponent, { centered: true, backdrop: 'static', keyboard: false, windowClass: 'modal-otp-custom' });
							// Prepare inputs for SMS mode
							modalRef.componentInstance.mode = 'sms';
							modalRef.componentInstance.phone = statusRes.phone || '';
							modalRef.componentInstance.operationRef = opId;
							modalRef.componentInstance.onResend.subscribe(() => {
								this.firmaService.startAuthOperation(id, token).subscribe({
									next: (newAuthRes: any) => {
										modalRef.componentInstance.operationRef = newAuthRes.operationID;
										modalRef.componentInstance.resetTimer();
									},
									error: () => {
										this.showError('Errore', "Impossibile ricaricare il codice OTP.");
									}
								});
							});

							modalRef.result.then((otpCode: any) => {
								if (otpCode) {
									this.isActionLoading.set(true);
									this.firmaService.openTransaction(id, token, parseInt(otpCode, 10), opId).subscribe({
										next: () => {
											this.isActionLoading.set(false);
											this.firmaService.setAuthenticated(id, opId);
											this.navigateToDettaglio(pratica, opId, true);
										},
										error: () => {
											this.isActionLoading.set(false);
											this.showError('Errore', 'Codice OTP non valido.');
										}
									});
								}
							}, () => {
								// User dismissed modal
							});
						} else {
							this.isActionLoading.set(true);
							this.firmaService.openTransaction(id, token, 0, opId).subscribe({
								next: () => {
									this.isActionLoading.set(false);
									this.firmaService.setAuthenticated(id, opId);
									this.navigateToDettaglio(pratica, opId, true);
								},
								error: () => {
									this.isActionLoading.set(false);
									this.showError('Errore', 'Impossibile aprire la transazione.');
								}
							});
						}
					},
					error: () => {
						this.isActionLoading.set(false);
						this.showError('Errore', "Impossibile avviare l'operazione di firma.");
					}
				});
			},
			error: () => {
				this.isActionLoading.set(false);
				this.showError('Errore', 'Impossibile recuperare i dettagli della pratica.');
			}
		});
	}

	navigateToDettaglio(pratica: PraticaDaFirmare, opId: string, authenticated: boolean = false) {
		this.router.navigate(['/app/dashboard/dettaglio', pratica.idTransaction], {
			state: { pratica, operationID: opId, authenticated }
		});
	}

	showError(title: string, message: string) {
		const modalRef = this.modalService.open(ModaleErroreGenericoComponent, { centered: true });
		modalRef.componentInstance.title = title;
		modalRef.componentInstance.message = message;
	}
}
